import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

type SortConfig = { key: string; direction: "ASC" | "DESC" };

export interface useDataTableStateStates {
  pageSizeOptions: number[]
  sortConfig: { key: string; direction: "ASC" | "DESC" };
  setSortConfig: (cfg: { key: string; direction: "ASC" | "DESC" }) => void;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  filter: string;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
}

export type DataTableStateOptions = {
  syncPageToUrl?: boolean;
  initialPage?: number;
  initialPageSize?: number;
};

export function useDataTableState(
  initialSortKey: string,
  pageSizeOptions?: number[],
  initialSortDirection: 'ASC' | 'DESC' = 'ASC',
  options?: DataTableStateOptions,
) {
  const resolvedPageSizeOptions = pageSizeOptions ?? [7, 12, 20, 50, 100, 500, 1000];
  const syncPageToUrl = options?.syncPageToUrl !== false;

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: initialSortKey,
    direction: initialSortDirection,
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = Number(searchParams.get("page"));
    if (pageParam && pageParam > 0)
      return pageParam
    if (options?.initialPage && options.initialPage > 0)
      return options.initialPage
    return 1
  })

  const [pageSize, setPageSize] = useState(() => {
    const requested = options?.initialPageSize;
    if (requested && resolvedPageSizeOptions.includes(requested))
      return requested;
    return resolvedPageSizeOptions[1];
  });
  const [filter, setFilter] = useState(() => searchParams.get("q") ?? "");

  useEffect(() => {
    if (!syncPageToUrl) return;
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", String(currentPage));
      if (filter.trim()) {
        newParams.set("q", filter);
      } else {
        newParams.delete("q");
      }
      return newParams;
    }, { replace: true }
    );
  }, [syncPageToUrl, currentPage, filter, setSearchParams]);

  const r: useDataTableStateStates = {
    pageSizeOptions: resolvedPageSizeOptions,
    sortConfig,
    setSortConfig,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    filter,
    setFilter,
  }

  return r;
}
